/* eslint-disable @typescript-eslint/naming-convention */

'use client';

import React, { memo, useState, useCallback, useEffect, useMemo } from 'react';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import 'swiper/css/virtual';
import {
  Pagination,
  Autoplay,
  Navigation,
  EffectFade,
  Controller,
  EffectCreative,
  FreeMode,
  Virtual,
  Keyboard,
} from 'swiper/modules';

import { isIOS } from 'react-device-detect';

import { Swiper, SwiperSlide } from 'swiper/react';
import { AutoplayOptions } from 'swiper/types/modules/autoplay';
import { PaginationOptions } from 'swiper/types/modules/pagination';
import { NavigationOptions } from 'swiper/types/modules/navigation';
import { CreativeEffectOptions } from 'swiper/types/modules/effect-creative';
import { ControllerOptions } from 'swiper/types/modules/controller';
import { VirtualOptions } from 'swiper/types/modules/virtual';
import { Swiper as SwiperClass } from 'swiper/types';

import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';

import * as S from './style';

/**
 * iOS 앱 웹뷰에서 앱 GNB(탭) 스와이프를 일시적으로 제한하도록 요청한다.
 *
 * 웹뷰 안의 가로 Swiper를 스와이프할 때 앱의 가로 제스처와 충돌해
 * 캐러셀 대신 앱 화면이 넘어가는 현상을 막기 위한 것으로,
 * 스와이프 제한/해제 제어는 iOS App에서 처리하므로 웹은 중지 요청만 보낸다.
 * iOS 앱 웹뷰가 아니면 아무 동작도 하지 않는다.
 */
const requestTabSwipeStop = () => {
  try {
    if (typeof window === 'undefined') return;

    const isAppWebView = navigator.userAgent.indexOf('wcknewapp') > -1;

    if (!isIOS || !isAppWebView) return;

    window.location.href = 'wconceptkr://tabSwipeStop';
  } catch (e) {
    console.error(e);
  }
};

interface IProps {
  children?: React.ReactNode;
  value?: string | number | undefined;
  selectedValue?: string | number | undefined;
  className?: string;
  spaceBetween?: number;
  speed?: number;
  slidesOffsetAfter?: number;
  slidesOffsetBefore?: number;
  resistanceRatio?: number;
  slidesPerView?: number | 'auto' | undefined;
  slidesPerGroup?: number;
  effect?: string;
  loop?: boolean;
  loopAddBlankSlides?: boolean;
  loopAdditionalSlides?: number;
  loopedPerView?: null | number;
  freeMode?: boolean | any;
  cssMode?: boolean | any;
  nested?: boolean;
  autoHeight?: boolean;
  centeredSlides?: boolean;
  /** centeredSlides 로 가운데 정렬하되 처음·마지막은 좌·우 끝에 붙인다 (loop·pagination 과 같이 쓰지 않는다) */
  centeredSlidesBounds?: boolean;
  watchSlidesProgress?: boolean;
  slideToClickedSlide?: boolean;
  simulateTouch?: boolean;
  direction?: 'horizontal' | 'vertical';
  width?: null | number;
  initialSlide?: number;
  lazyPreloadPrevNext?: number;
  navigation?: boolean | NavigationOptions;
  navigationEl?: React.JSX.Element;
  autoplay?: boolean | AutoplayOptions;
  pagination?: boolean | PaginationOptions;
  paginationEl?: React.JSX.Element;
  creativeEffect?: boolean | CreativeEffectOptions;
  controller?: ControllerOptions | undefined;
  allowTouchMove?: boolean;
  shortSwipes?: boolean;
  longSwipes?: boolean;
  preventInteractionOnTransition?: boolean;
  touchRatio?: number;
  isActiveSlide?: boolean;
  /**
   * Fragment 로 감싼 슬라이드(세트 단위)에서, 지금 활성인 슬라이드 안의 아이템만
   * isActive 를 살린다. 루프용 복제 슬라이드가 같은 isActive 를 물고 가서
   * 한 아이템이 여러 벌 활성화되는 걸 막는다. (슬라이드를 1장씩 보여주는 모듈용)
   */
  activeSlideItemsOnly?: boolean;
  virtual?: boolean | VirtualOptions;
  keyboard?: any;
  onSwiper?: (swiper: SwiperClass) => void;
  onTouchStart?: (swiper: SwiperClass) => void;
  onTouchEnd?: (swiper: SwiperClass) => void;
  onSliderFirstMove?: (swiper: SwiperClass) => void;
  onTransitionEnd?: (swiper: SwiperClass) => void;
  onSlideChange?: (swiper: SwiperClass) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}
interface SlideProps {
  value?: any;
  'data-swiper-slide-duplicate'?: boolean;
  parentSwiperRef?: any;
  isActive?: boolean;
  key?: string | number;
  className?: string;
  children?: React.ReactNode;
}

const ItemsSwiper: React.FunctionComponent<IProps> = ({
  children,
  value,
  selectedValue,
  className,
  spaceBetween = 0,
  speed = 300,
  slidesOffsetAfter = 0,
  slidesOffsetBefore = 0,
  resistanceRatio = 0.85,
  slidesPerView = 1,
  slidesPerGroup = 1,
  effect = 'slide',
  loop = true,
  loopAddBlankSlides = true,
  loopAdditionalSlides = 0,
  loopedPerView = null,
  freeMode = false,
  cssMode = false,
  nested = false,
  autoHeight = false,
  centeredSlides = false,
  centeredSlidesBounds = false,
  watchSlidesProgress = false,
  slideToClickedSlide = false,
  simulateTouch = true,
  width = null,
  navigation = false,
  navigationEl,
  autoplay,
  pagination,
  initialSlide = 0,
  paginationEl,
  lazyPreloadPrevNext = 0,
  creativeEffect,
  controller,
  allowTouchMove = true,
  shortSwipes = true,
  longSwipes = true,
  direction = 'horizontal',
  preventInteractionOnTransition = false,
  isActiveSlide = true,
  activeSlideItemsOnly = false,
  virtual = false,
  touchRatio = 1,
  keyboard = {
    enabled: false,
  },
  onSwiper,
  onTouchStart,
  onTouchEnd,
  onSliderFirstMove,
  onTransitionEnd,
  onSlideChange,
  onMouseEnter,
  onMouseLeave,
}: IProps) => {
  const [_selectedValue, setSelectedValue] = useState(selectedValue);
  const [currentSwiper, setCurrentSwiper] = useState<SwiperClass>();
  const childrenLength = children instanceof Array ? children?.length : 0;
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  // EVENT HANDLER : onSwiperSlideTo
  const onSwiperSlideTo = useCallback(
    (swiper: SwiperClass) => {
      if (children instanceof Array && _selectedValue !== undefined) {
        const idx = children?.findIndex(
          (v: { props: { value: string | number | undefined } }) =>
            v.props.value === _selectedValue
        );
        if (swiper && idx !== -1 && swiper.params && swiper.realIndex !== idx)
          swiper.slideToLoop(idx, speed, false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [_selectedValue, speed]
  );

  useEffect(() => {
    setSelectedValue(selectedValue);
  }, [selectedValue]);

  useEffect(() => {
    if (_selectedValue) if (currentSwiper) onSwiperSlideTo(currentSwiper);
  }, [_selectedValue, currentSwiper, onSwiperSlideTo]);

  const preparedChildren = useMemo(() => {
    if (!children || !Array.isArray(children)) return children;

    const childrenArray = children as React.ReactElement<SlideProps>[];
    const childCount = childrenArray.length;

    // virtual 모드일 때는 복제하지 않음
    if (virtual) {
      return childrenArray;
    }

    if (
      loop &&
      childCount > 1 &&
      typeof loopedPerView === 'number' &&
      loopedPerView > 1 &&
      loopedPerView + 3 > childCount
    ) {
      const duplicateCount = childCount * 2;
      const duplicates: React.ReactElement<SlideProps>[] = [];
      for (let i = 0; i < duplicateCount; i++) {
        const originalIndex = i % childCount;
        const child = childrenArray[originalIndex];
        if (child.type !== React.Fragment) {
          duplicates.push(
            React.cloneElement(child, {
              key: `duplicate-${i}`,
              'data-swiper-slide-duplicate': true,
            })
          );
        } else {
          duplicates.push(
            React.cloneElement(child, {
              key: `duplicate-${i}`,
            })
          );
        }
      }

      return [...childrenArray, ...duplicates];
    }

    return childrenArray;
  }, [children, loop, loopedPerView, virtual]);

  /**
   * Fragment 로 감싼 슬라이드는 안쪽 아이템의 isActive 를 모듈이 직접 정하므로
   * 기본적으로는 덮어쓰지 않는다(undefined).
   *
   * 다만 loop 용 복제 슬라이드에도 같은 값이 그대로 복사돼서
   * 같은 아이템이 여러 슬라이드에서 동시에 활성화된다.
   * (동영상 아이템이면 플레이어가 복제 수만큼 생성된다)
   * activeSlideItemsOnly 를 켜면 지금 활성인 슬라이드 안에서만 isActive 를 살린다.
   */
  const getFragmentProps = useCallback(
    (v: React.ReactElement<SlideProps>, i: number) => {
      if (!activeSlideItemsOnly) return undefined;

      const isSlideActive = i === activeSlideIndex;

      return {
        children: React.Children.map(v.props?.children, child => {
          if (!React.isValidElement(child)) return child;

          const item = child as React.ReactElement<{ isActive?: boolean }>;

          return React.cloneElement(item, {
            isActive: isSlideActive && !!item.props?.isActive,
          });
        }),
      };
    },
    [activeSlideItemsOnly, activeSlideIndex]
  );

  const getListRender2 = useCallback(
    (el: React.ReactNode) => {
      if (Array.isArray(el)) {
        return el.map((v: React.ReactElement<SlideProps>, i) => {
          if (v) {
            return (
              <SwiperSlide
                // virtualIndex를 key로 쓰면 Swiper 11 virtual+loop에서 숫자 key(1,2) 중복 경고 발생
                // eslint-disable-next-line react/no-array-index-key
                key={`slide-${i}-${String(v.key)}`}
                virtualIndex={virtual ? i : undefined}
                data-value={v.props?.value ? v.props?.value : null}
                className={`items-swiper-slide ${
                  v.props['data-swiper-slide-duplicate']
                    ? 'swiper-slide-duplicate'
                    : ''
                }`}
              >
                {React.cloneElement(
                  v,
                  v.type === React.Fragment
                    ? getFragmentProps(v, i)
                    : {
                        parentSwiperRef: currentSwiper,
                        isActive: isActiveSlide
                          ? i === activeSlideIndex
                          : false,
                      }
                )}
              </SwiperSlide>
            );
          }
          return null;
        });
      }
      return null;
    },
    [activeSlideIndex, currentSwiper, getFragmentProps, isActiveSlide, virtual]
  );

  return (
    <S.Box
      className={classNameBind(['items-swiper', className || ''])}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Swiper
        className="items-swiper-swiper"
        modules={[
          Pagination,
          Autoplay,
          Navigation,
          EffectFade,
          Controller,
          EffectCreative,
          FreeMode,
          Virtual,
          Keyboard,
        ]}
        pagination={pagination}
        spaceBetween={spaceBetween}
        speed={speed}
        slidesPerView={slidesPerView}
        slidesPerGroup={slidesPerGroup}
        loop={childrenLength < 2 ? false : loop}
        loopAddBlankSlides={loopAddBlankSlides}
        loopAdditionalSlides={loopAdditionalSlides}
        freeMode={freeMode}
        cssMode={cssMode}
        nested={nested}
        autoHeight={autoHeight}
        resistanceRatio={resistanceRatio}
        autoplay={childrenLength < 2 ? false : autoplay}
        navigation={navigation}
        width={width}
        effect={effect}
        simulateTouch={simulateTouch}
        lazyPreloadPrevNext={lazyPreloadPrevNext}
        slidesOffsetBefore={slidesOffsetBefore}
        slidesOffsetAfter={slidesOffsetAfter}
        slideToClickedSlide={slideToClickedSlide}
        centeredSlides={centeredSlides}
        centeredSlidesBounds={centeredSlidesBounds}
        direction={direction}
        initialSlide={initialSlide}
        touchRatio={touchRatio}
        watchSlidesProgress={watchSlidesProgress}
        creativeEffect={
          typeof creativeEffect === 'boolean' ? undefined : creativeEffect
        }
        controller={controller}
        allowTouchMove={allowTouchMove}
        shortSwipes={shortSwipes}
        longSwipes={longSwipes}
        virtual={virtual}
        keyboard={keyboard}
        onSlideChange={onSlideChange}
        onTouchStart={(swiper: SwiperClass) => {
          // iOS 앱 웹뷰: 앱 탭(GNB) 스와이프를 일시 중지시켜 제스처 충돌을 막는다.
          // (해제는 앱에서 처리)
          //
          // sliderFirstMove(가로 스와이프 확정 시점)는 손가락이 이미 움직인 뒤라
          // 앱 제스처 인식기가 먼저 시작돼 늦는 것으로 보여, 터치 다운 시점으로 앞당김.
          // 대신 탭·세로 스크롤에서도 호출되므로 앱 측 해제 로직이 이를 감당해야 함.
          if (direction === 'horizontal') {
            requestTabSwipeStop();
          }

          if (onTouchStart) {
            onTouchStart(swiper);
          }
        }}
        onTouchEnd={onTouchEnd}
        onSliderFirstMove={onSliderFirstMove}
        preventInteractionOnTransition={preventInteractionOnTransition}
        onInit={(swiper: SwiperClass) => {
          setCurrentSwiper(swiper);
          onSwiperSlideTo(swiper);
        }}
        onTransitionEnd={(swiper: SwiperClass) => {
          if (onTransitionEnd) {
            onTransitionEnd(swiper);
          }

          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          setActiveSlideIndex(swiper.realIndex);
        }}
        onSwiper={(swiper: SwiperClass) => {
          if (onSwiper) {
            onSwiper(swiper);
          }
        }}
      >
        {getListRender2(preparedChildren)}
      </Swiper>
      {paginationEl}
      {childrenLength > 1 && navigationEl}
    </S.Box>
  );
};

export default memo(ItemsSwiper);
